import { Box, Flex, Grid, Heading, Text, Icon } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { LuCalendarDays, LuBrain, LuShieldCheck, LuZap, LuStethoscope, LuUsers } from "react-icons/lu";
import { IconType } from "react-icons";
import { useLandingTheme, LandingTheme } from "../Index";

const MotionBox = motion.create(Box);

// Feature Card Component
const FeatureCard = ({
  icon,
  title,
  description,
  delay,
  theme,
  isDark,
}: {
  icon: IconType;
  title: string;
  description: string;
  delay: number;
  theme: LandingTheme;
  isDark: boolean;
}) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, delay }}
  >
    <Box
      p={6}
      borderRadius="2xl"
      bg={isDark ? `${theme.surfaceMuted}80` : theme.surface}
      border="1px solid"
      borderColor={theme.border}
      transition="all 0.3s"
      boxShadow={isDark ? "none" : "sm"}
      _hover={{
        borderColor: `${theme.primary}60`,
        boxShadow: `0 10px 40px ${theme.primary}${isDark ? "26" : "15"}`,
        transform: "translateY(-2px)",
      }}
    >
      <Flex align="start" gap={4}>
        <Box p={3} borderRadius="xl" bg={`${theme.primary}${isDark ? "1a" : "10"}`} color={theme.primary}>
          <Icon as={icon} boxSize={6} />
        </Box>
        <Box>
          <Heading as="h3" fontSize="lg" fontWeight="bold" color={theme.textPrimary} mb={2}>
            {title}
          </Heading>
          <Text fontSize="sm" color={theme.textSecondary} lineHeight="relaxed">
            {description}
          </Text>
        </Box>
      </Flex>
    </Box>
  </MotionBox>
);

// Timeline Event Component
const TimelineEvent = ({
  isFocus,
  icon,
  label,
  title,
  time,
  duration,
  theme,
  isDark,
}: {
  isFocus: boolean;
  icon: IconType;
  label: string;
  title: string;
  time: string;
  duration: string;
  theme: LandingTheme;
  isDark: boolean;
}) => (
  <Box position="relative">
    {/* Timeline Dot */}
    <Box
      position="absolute"
      left="-39px"
      top={0}
      p={1.5}
      borderRadius="full"
      zIndex={10}
      bg={isFocus ? theme.primary : theme.surfaceMuted}
      border={isFocus ? "none" : "1px solid"}
      borderColor={theme.border}
      color={isFocus ? (isDark ? theme.bg : "white") : theme.textSecondary}
      boxShadow={isFocus ? `0 0 10px ${theme.primaryGlow}` : "none"}
    >
      <Icon as={icon} boxSize={3.5} display="block" />
    </Box>

    {/* Event Card */}
    <Box
      bg={
        isFocus
          ? isDark
            ? `${theme.surfaceMuted}cc`
            : `${theme.primary}08`
          : isDark
            ? `${theme.surfaceMuted}4d`
            : theme.surface
      }
      p={4}
      borderRadius="xl"
      borderLeft={isFocus ? "4px solid" : "none"}
      borderLeftColor={isFocus ? theme.primary : undefined}
      border={isFocus ? undefined : "1px solid"}
      borderColor={isFocus ? undefined : theme.border}
    >
      <Flex justify="space-between" align="start" mb={1}>
        <Text
          color={isFocus ? theme.primary : theme.textSecondary}
          fontWeight="bold"
          fontSize="sm"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          {label}
        </Text>
        <Text fontSize="xs" color={theme.textSecondary}>
          {duration}
        </Text>
      </Flex>
      <Text color={theme.textPrimary} fontWeight="medium" mb={1}>
        {title}
      </Text>
      <Text fontSize="xs" color={theme.textSecondary}>
        {time}
      </Text>
    </Box>
  </Box>
);

export const Features = () => {
  const { theme, isDark } = useLandingTheme();

  return (
    <Box
      as="section"
      position="relative"
      py={20}
      px={4}
      w="full"
      bg={isDark ? `${theme.surface}4d` : theme.surfaceMuted}
      borderY="1px solid"
      borderColor={theme.border}
      id="features"
      transition="background-color 0.3s"
    >
      <Grid maxW="6xl" mx="auto" templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={16} alignItems="center">
        {/* Text Content - Left Side */}
        <Flex direction="column" gap={8} order={{ base: 2, lg: 1 }}>
          <Box>
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="bold"
              color={theme.textPrimary}
              letterSpacing="tight"
              mb={4}
            >
              Why Muro Works
            </Heading>
            <Text fontSize="xl" color={theme.textSecondary} fontWeight="light" lineHeight="relaxed">
              We combine behavioral science with smart automation to help you reclaim your time without the guilt.
            </Text>
          </Box>

          <Flex direction="column" gap={6}>
            <FeatureCard
              icon={LuCalendarDays}
              title="Smart Scheduling"
              description="Automatically finds gaps in your calendar for deep work. No more manual planning or context switching."
              delay={0}
              theme={theme}
              isDark={isDark}
            />
            <FeatureCard
              icon={LuBrain}
              title="Defeat Procrastination"
              description="Research-backed time blocking reduces decision fatigue and helps you stay on track with your goals."
              delay={0.1}
              theme={theme}
              isDark={isDark}
            />
            <FeatureCard
              icon={LuShieldCheck}
              title="Reduce Stress"
              description="Protect your focus time to prevent burnout and reduce anxiety. Muro defends your calendar."
              delay={0.2}
              theme={theme}
              isDark={isDark}
            />
          </Flex>
        </Flex>

        {/* Timeline Visual - Right Side */}
        <Flex position="relative" order={{ base: 1, lg: 2 }} h="full" align="center" justify="center">
          {/* Background Glow */}
          <Box
            position="absolute"
            inset={0}
            bg={`${theme.primary}${isDark ? "1a" : "08"}`}
            filter="blur(100px)"
            borderRadius="full"
            pointerEvents="none"
          />

          {/* Timeline Card */}
          <MotionBox
            w="full"
            maxW="md"
            bg={theme.cardBg}
            border="1px solid"
            borderColor={theme.border}
            borderRadius="3xl"
            p={6}
            boxShadow={isDark ? "2xl" : "0 25px 50px -12px rgba(0, 0, 0, 0.1)"}
            position="relative"
            overflow="hidden"
            zIndex={10}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Header */}
            <Flex align="center" justify="space-between" mb={6} px={2}>
              <Text color={theme.textPrimary} fontWeight="medium">
                Today&apos;s Flow
              </Text>
              <Text color={theme.textSecondary} fontSize="lg">
                ⋯
              </Text>
            </Flex>

            {/* Timeline */}
            <Box position="relative" pl={8} borderLeft="1px solid" borderColor={theme.border} ml={3} py={2}>
              <Flex direction="column" gap={8}>
                <TimelineEvent
                  isFocus={true}
                  icon={LuZap}
                  label="Focus Time"
                  title="Deep Work Session"
                  time="9:00am - 10:30am"
                  duration="1h 30m"
                  theme={theme}
                  isDark={isDark}
                />
                <TimelineEvent
                  isFocus={false}
                  icon={LuStethoscope}
                  label="Appointment"
                  title="Doctor's Appointment"
                  time="11:00am - 12:00pm"
                  duration="1h"
                  theme={theme}
                  isDark={isDark}
                />
                <TimelineEvent
                  isFocus={true}
                  icon={LuZap}
                  label="Focus Time"
                  title="Project Planning"
                  time="1:00pm - 2:00pm"
                  duration="1h"
                  theme={theme}
                  isDark={isDark}
                />
                <TimelineEvent
                  isFocus={false}
                  icon={LuUsers}
                  label="Meeting"
                  title="Standup Call"
                  time="4:00pm - 5:00pm"
                  duration="1h"
                  theme={theme}
                  isDark={isDark}
                />
              </Flex>
            </Box>
          </MotionBox>
        </Flex>
      </Grid>
    </Box>
  );
};
